import { type HTMLAttributes, type KeyboardEvent, useEffect } from 'react';

import { helpUrl, MAIN_NAV_ITEMS } from '../../config/navigation';
import { useAuth } from '../../state/auth';
import './mobile-drawer.css';

export type MobileDrawerVariant = 'no log in' | 'carrier' | 'owner' | 'logged in';
export type MobileDrawerType = MobileDrawerVariant;

export type MobileDrawerAction =
  | 'logo'
  | 'close'
  | 'login'
  | 'carrier-registration'
  | 'personal-cabinet'
  | 'how-it-works'
  | 'why-lovo'
  | 'reviews'
  | 'help'
  | 'owner-requests'
  | 'owner-messages'
  | 'owner-notifications'
  | 'owner-active-transports'
  | 'owner-profile'
  | 'owner-settings'
  | 'carrier-dashboard'
  | 'carrier-my-offers'
  | 'carrier-chat-list'
  | 'carrier-notifications'
  | 'carrier-active-transport'
  | 'carrier-profile-internal'
  | 'carrier-settings'
  | 'carrier-documents'
  | 'carrier-help'
  | 'carrier-search'
  | 'carrier-offers'
  | 'carrier-active-transports'
  | 'carrier-history'
  | 'carrier-profile'
  | 'carrier-billing';

export interface MobileDrawerProps extends HTMLAttributes<HTMLElement> {
  variant?: MobileDrawerVariant;
  activeAction?: MobileDrawerAction;
  onClose?: () => void;
  onLogoClick?: () => void;
  onNavigate?: (action: MobileDrawerAction) => void;
}

interface DrawerItem {
  action: MobileDrawerAction;
  label: string;
  node?: string;
  bold?: boolean;
  href?: string;
}

interface DrawerDefinition {
  root: string;
  top: string;
  logo: string;
  close: string;
  content: string;
  divider: string;
  className: string;
  primary: readonly DrawerItem[];
  secondary: readonly DrawerItem[];
}

const PUBLIC_NAV_DRAWER_ITEMS: DrawerItem[] = MAIN_NAV_ITEMS.map((item, idx) => ({
  action: item.id as MobileDrawerAction,
  label: item.label,
  node: `1:1246${5 + idx}`,
  href: item.href || undefined,
}));

const PUBLIC_ITEMS = {
  noLogin: PUBLIC_NAV_DRAWER_ITEMS,
  loggedIn: PUBLIC_NAV_DRAWER_ITEMS,
};

const CABINET_ROUTES: Partial<Record<MobileDrawerAction, string>> = {
  'owner-requests': 'my-requests',
  'owner-messages': 'chat-list',
  'owner-notifications': 'notifications',
  'owner-active-transports': 'active-transport',
  'owner-profile': 'profile-internal',
  'owner-settings': 'settings',
  'carrier-dashboard': 'carrier-dashboard',
  'carrier-my-offers': 'carrier-my-offers',
  'carrier-chat-list': 'carrier-chat-list',
  'carrier-notifications': 'carrier-notifications',
  'carrier-active-transport': 'carrier-active-transport',
  'carrier-profile-internal': 'carrier-profile-internal',
  'carrier-settings': 'carrier-settings',
  'carrier-documents': 'carrier-documents',
  'carrier-help': 'carrier-help',
};

const DRAWERS: Record<MobileDrawerVariant, DrawerDefinition> = {
  'no log in': {
    root: '1:12455',
    top: '1:12456',
    logo: '1:12457',
    close: '1:12458',
    content: '1:12459',
    divider: '1:12463',
    className: 'no-log-in',
    primary: [
      { action: 'login', label: 'Войти', node: '1:12461', bold: true, href: '/portal/#login' },
      { action: 'carrier-registration', label: 'Стать перевозчиком', node: '1:12462', href: '/portal/#carrier-rega-email' },
    ],
    secondary: PUBLIC_ITEMS.noLogin,
  },
  carrier: {
    root: '1:12469',
    top: '1:12470',
    logo: '1:12471',
    close: '1:12472',
    content: '1:12473',
    divider: '1:12480',
    className: 'carrier',
    primary: [
      { action: 'carrier-dashboard', label: 'Главная', node: '1:12475' },
      { action: 'carrier-my-offers', label: 'Мои отклики', node: '1:12476' },
      { action: 'carrier-chat-list', label: 'Сообщения', node: '1:12477' },
      { action: 'carrier-notifications', label: 'Уведомления', node: '1:12478' },
      { action: 'carrier-active-transport', label: 'Активные перевозки', node: '1:12479' },
    ],
    secondary: [
      { action: 'carrier-profile-internal', label: 'Профиль', node: '1:12482' },
      { action: 'carrier-settings', label: 'Настройки' },
      { action: 'carrier-documents', label: 'Документы', node: '1:12484' },
      { action: 'carrier-help', label: 'Помощь', node: '1:12485' },
    ],
  },
  owner: {
    root: '155:42513',
    top: '155:42514',
    logo: '155:42515',
    close: '155:42516',
    content: '155:42517',
    divider: '155:42524',
    className: 'owner',
    primary: [
      { action: 'personal-cabinet', label: 'Личный кабинет', node: '155:42519', bold: true },
      { action: 'owner-requests', label: 'Мои заявки', node: '155:42520' },
      { action: 'owner-messages', label: 'Сообщения', node: '155:42521' },
      { action: 'owner-notifications', label: 'Уведомления', node: '155:42522' },
      { action: 'owner-active-transports', label: 'Активные перевозки', node: '155:42523' },
    ],
    secondary: [
      { action: 'owner-profile', label: 'Профиль', node: '155:42526' },
      { action: 'owner-settings', label: 'Настройки', node: '155:42528' },
      { action: 'help', label: 'Помощь', node: '155:42529' },
    ],
  },
  'logged in': {
    root: '137:41942',
    top: '137:41943',
    logo: '137:41944',
    close: '137:41945',
    content: '137:41946',
    divider: '137:41950',
    className: 'logged-in',
    primary: [
      { action: 'personal-cabinet', label: 'Личный кабинет', node: '137:41948', bold: true },
    ],
    secondary: PUBLIC_ITEMS.loggedIn,
  },
};

interface DrawerControlProps {
  item: DrawerItem;
  isActive?: boolean;
  onNavigate?: (action: MobileDrawerAction) => void;
  onClose?: () => void;
}

function DrawerControl({ item, isActive, onNavigate, onClose }: DrawerControlProps) {
  const isBold = isActive !== undefined ? isActive : Boolean(item.bold);
  const classes = [
    'lovo-mobile-drawer__link',
    isBold ? 'lovo-mobile-drawer__link--bold' : '',
    isActive ? 'is-active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => {
    onNavigate?.(item.action);
    onClose?.();
  };

  if (item.href) {
    return (
      <a
        className={classes}
        href={item.href}
        onClick={handleClick}
        data-figma-node={item.node}
      >
        {item.label}
      </a>
    );
  }

  return (
    <button
      className={classes}
      type="button"
      onClick={handleClick}
      data-figma-node={item.node}
    >
      {item.label}
    </button>
  );
}

export function MobileDrawer({
  variant = 'no log in',
  activeAction,
  onClose,
  onLogoClick,
  onNavigate,
  onKeyDown,
  className = '',
  'aria-label': ariaLabel,
  ...props
}: MobileDrawerProps) {
  const { user, status } = useAuth();
  const drawer = DRAWERS[variant] ?? DRAWERS['no log in'];

  function routedItem(item: DrawerItem): DrawerItem {
    if (item.action === 'help') return { ...item, href: helpUrl(status === 'authenticated' ? user?.active_role : null) };
    const route = item.action === 'personal-cabinet'
      ? (variant === 'carrier' ? 'carrier-dashboard' : 'my-requests')
      : CABINET_ROUTES[item.action];
    return route ? { ...item, href: `/portal/#${route}` } : item;
  }

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (!event.defaultPrevented && event.key === 'Escape') onClose?.();
  };

  return (
    <aside
      {...props}
      className={[
        'lovo-mobile-drawer',
        `lovo-mobile-drawer--${drawer.className}`,
        className,
      ].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? 'Мобильная навигация'}
      onKeyDown={handleKeyDown}
      data-figma-node={drawer.root}
      data-figma-variant={`Type=${variant}`}
    >
      <div className="lovo-mobile-drawer__top" data-figma-node={drawer.top}>
        <a
          className="lovo-mobile-drawer__brand"
          href={window.location.hash && window.location.hash !== '#landing' && window.location.hash !== '#top' ? '#landing' : '#top'}
          aria-label="LOVO"
          onClick={(e) => {
            if (onLogoClick) {
              e.preventDefault();
              onLogoClick();
            }
            onNavigate?.('logo');
            onClose?.();
          }}
          data-figma-node={drawer.logo}
          data-figma-variant="Property 1=Variant2"
        >
          <img src="/figma/assets/logo-light.svg" alt="LOVO" />
        </a>

        <button
          className="lovo-mobile-drawer__close"
          type="button"
          aria-label="Закрыть меню"
          onClick={() => {
            onClose?.();
            onNavigate?.('close');
          }}
          data-figma-node={drawer.close}
        >
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            data-figma-node="1:13906"
          >
            <circle cx="18" cy="18" r="15.5" stroke="#ffffff" strokeWidth="1.5" />
            <path d="M13 13L23 23M23 13L13 23" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <nav
        className="lovo-mobile-drawer__content"
        aria-label="Мобильное меню"
        data-figma-node={drawer.content}
      >
        <div className="lovo-mobile-drawer__primary">
          {drawer.primary.map((item) => (
            <DrawerControl
              key={item.action}
              item={routedItem(item)}
              isActive={activeAction !== undefined ? item.action === activeAction : undefined}
              onNavigate={onNavigate}
              onClose={onClose}
            />
          ))}
        </div>

        <div className="lovo-mobile-drawer__divider" data-figma-node={drawer.divider} />

        <div className="lovo-mobile-drawer__secondary">
          {drawer.secondary.map((item) => (
            <DrawerControl
              key={item.action}
              item={routedItem(item)}
              isActive={activeAction !== undefined ? item.action === activeAction : undefined}
              onNavigate={onNavigate}
              onClose={onClose}
            />
          ))}
        </div>
      </nav>
    </aside>
  );
}

